(() => {
  if (typeof window.SEQReelsInit === 'function') {
    window.SEQReelsInit(document);
    return;
  }

  const SELECTOR = '[data-seq-reels]';

  class SEQReels {
    constructor(section) {
      if (!section || section.dataset.reelsInitialised === 'true') return;

      this.section = section;
      this.section.dataset.reelsInitialised = 'true';
      this.track = section.querySelector('[data-reels-track]');
      this.cards = [...section.querySelectorAll('[data-reel-card]')];
      this.progress = section.querySelector('[data-reels-progress]');
      this.modal = section.querySelector('[data-reels-modal]');
      this.modalVideo = section.querySelector('[data-modal-video]');
      this.modalTitle = section.querySelector('[data-modal-title]');
      this.modalLocation = section.querySelector('[data-modal-location]');
      this.soundButton = section.querySelector('[data-reels-sound]');
      this.volumeSlider = section.querySelector('[data-reels-volume]');
      this.volumeValue = section.querySelector('[data-reels-volume-value]');
      this.playButton = section.querySelector('[data-modal-play]');
      this.currentIndex = 0;
      this.modalIndex = 0;
      this.autoAdvanceTimer = null;
      this.isPointerDown = false;
      this.previousFocus = null;
      this.volume = this.getInitialVolume();
      this.lastAudibleVolume = this.volume > 0 ? this.volume : 0.8;
      this.isMuted = false;
      this.previewLoopSeconds = this.getPreviewLoopSeconds();

      if (!this.track || !this.cards.length) return;

      this.bindEvents();
      this.updateActiveFromScroll();
      this.playAllCardPreviews(true);
      this.startAutoAdvance();
    }

    bindEvents() {
      this.section.querySelectorAll('[data-reels-prev]').forEach((button) => {
        button.addEventListener('click', () => this.move(-1, true));
      });

      this.section.querySelectorAll('[data-reels-next]').forEach((button) => {
        button.addEventListener('click', () => this.move(1, true));
      });

      this.cards.forEach((card, index) => {
        const openButton = card.querySelector('[data-reel-open]');
        const previewVideo = card.querySelector('video');

        if (openButton && !openButton.disabled) {
          openButton.addEventListener('click', () => this.openModal(index));
        }

        if (previewVideo) {
          previewVideo.muted = true;
          previewVideo.playsInline = true;

          previewVideo.addEventListener('timeupdate', () => {
            this.enforcePreviewLoop(previewVideo);
          });

          previewVideo.addEventListener('ended', () => {
            this.restartPreview(previewVideo);
          });
        }
      });

      let scrollFrame = null;
      this.track.addEventListener('scroll', () => {
        if (scrollFrame) cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => this.updateActiveFromScroll());
      }, { passive: true });

      this.track.addEventListener('pointerdown', () => {
        this.isPointerDown = true;
        this.stopAutoAdvance();
      });

      window.addEventListener('pointerup', () => {
        if (!this.isPointerDown) return;
        this.isPointerDown = false;
        this.startAutoAdvance();
      }, { passive: true });

      this.track.addEventListener('mouseenter', () => this.stopAutoAdvance());
      this.track.addEventListener('mouseleave', () => this.startAutoAdvance());
      this.track.addEventListener('focusin', () => this.stopAutoAdvance());
      this.track.addEventListener('focusout', () => this.startAutoAdvance());

      this.section.querySelectorAll('[data-reels-close]').forEach((button) => {
        button.addEventListener('click', () => this.closeModal());
      });

      this.section.querySelectorAll('[data-reels-close-after-booking]').forEach((button) => {
        button.addEventListener('click', () => {
          window.setTimeout(() => this.closeModal(), 80);
        });
      });

      this.section.querySelector('[data-modal-prev]')?.addEventListener('click', () => {
        this.showModalReel(this.modalIndex - 1);
      });

      this.section.querySelector('[data-modal-next]')?.addEventListener('click', () => {
        this.showModalReel(this.modalIndex + 1);
      });

      this.soundButton?.addEventListener('click', () => this.toggleSound());
      this.volumeSlider?.addEventListener('input', () => this.setVolumeFromSlider());
      this.volumeSlider?.addEventListener('change', () => this.setVolumeFromSlider());
      this.playButton?.addEventListener('click', () => this.toggleModalPlayback());
      this.modalVideo?.addEventListener('click', () => this.toggleModalPlayback());
      this.modalVideo?.addEventListener('play', () => this.updatePlayButton(false));
      this.modalVideo?.addEventListener('pause', () => this.updatePlayButton(true));

      document.addEventListener('keydown', (event) => {
        if (!this.modal || this.modal.hidden) return;

        if (event.key === 'Escape') this.closeModal();
        if (event.key === 'ArrowLeft') this.showModalReel(this.modalIndex - 1);
        if (event.key === 'ArrowRight') this.showModalReel(this.modalIndex + 1);
        if (event.key === 'Tab') this.keepFocusInsideModal(event);
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseCardVideos();
          this.stopAutoAdvance();
        } else if (!this.modal || this.modal.hidden) {
          this.playAllCardPreviews(true);
          this.startAutoAdvance();
        }
      });
    }

    getInitialVolume() {
      const rawValue = Number(this.volumeSlider?.value ?? 80);
      const normalised = Number.isFinite(rawValue) ? rawValue / 100 : 0.8;
      return Math.min(1, Math.max(0, normalised));
    }

    getPreviewLoopSeconds() {
      const value = Number(this.section.dataset.previewLoopSeconds || 8);
      return Number.isFinite(value) ? Math.max(1, value) : 8;
    }

    getPreviewEnd(video) {
      const duration = Number(video?.duration);
      if (Number.isFinite(duration) && duration > 0) {
        return Math.min(this.previewLoopSeconds, duration);
      }
      return this.previewLoopSeconds;
    }

    resetPreview(video) {
      if (!video) return;
      try {
        video.currentTime = 0;
      } catch (error) {
        // The video metadata may not be ready yet. It will start at zero naturally.
      }
    }

    restartPreview(video) {
      if (!video) return;
      if (this.modal && !this.modal.hidden) return;

      this.resetPreview(video);
      const playAttempt = video.play();
      playAttempt?.catch?.(() => {});
    }

    enforcePreviewLoop(video) {
      if (!video) return;
      if (this.modal && !this.modal.hidden) return;

      const previewEnd = this.getPreviewEnd(video);
      if (previewEnd <= 0 || video.currentTime < previewEnd - 0.08) return;

      this.resetPreview(video);
      if (video.paused && !document.hidden) {
        const playAttempt = video.play();
        playAttempt?.catch?.(() => {});
      }
    }

    getAutoplayEnabled() {
      return this.section.dataset.autoAdvance === 'true' &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    getInterval() {
      const value = Number(this.section.dataset.autoplayInterval || 6000);
      return Number.isFinite(value) ? Math.max(value, 4000) : 6000;
    }

    startAutoAdvance() {
      if (!this.getAutoplayEnabled() || this.cards.length < 2 || this.autoAdvanceTimer) return;
      if (this.modal && !this.modal.hidden) return;

      this.autoAdvanceTimer = window.setInterval(() => {
        this.move(1, false);
      }, this.getInterval());
    }

    stopAutoAdvance() {
      if (!this.autoAdvanceTimer) return;
      window.clearInterval(this.autoAdvanceTimer);
      this.autoAdvanceTimer = null;
    }

    restartAutoAdvance() {
      this.stopAutoAdvance();
      this.startAutoAdvance();
    }

    move(direction, userInitiated = false) {
      const nextIndex = (this.currentIndex + direction + this.cards.length) % this.cards.length;
      this.scrollToCard(nextIndex);
      if (userInitiated) this.restartAutoAdvance();
    }

    scrollToCard(index) {
      const card = this.cards[index];
      if (!card) return;

      const left = card.offsetLeft - this.track.offsetLeft;
      this.track.scrollTo({
        left,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });

      this.currentIndex = index;
      this.updateProgress();
      window.setTimeout(() => this.playAllCardPreviews(false), 280);
    }

    updateActiveFromScroll() {
      let closestIndex = 0;
      let closestDistance = Infinity;
      const trackLeft = this.track.getBoundingClientRect().left;

      this.cards.forEach((card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== this.currentIndex) this.currentIndex = closestIndex;
      this.cards.forEach((card, index) => card.classList.toggle('is-active', index === this.currentIndex));
      this.updateProgress();
    }

    updateProgress() {
      if (!this.progress) return;
      const percent = ((this.currentIndex + 1) / this.cards.length) * 100;
      this.progress.style.width = `${percent}%`;
    }

    pauseCardVideos() {
      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        if (video) {
          video.pause();
          this.resetPreview(video);
        }
        card.classList.remove('is-playing');
      });
    }

    playAllCardPreviews(restart = false) {
      if (this.modal && !this.modal.hidden) return;
      if (document.hidden) return;

      const previews = [];

      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        if (!video) return;

        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;

        if (restart || video.currentTime >= this.getPreviewEnd(video) - 0.08) {
          this.resetPreview(video);
        }

        previews.push({ card, video });
      });

      // Start every muted preview in the same task so they begin together.
      previews.forEach(({ card, video }) => {
        const playPromise = video.play();
        if (playPromise?.then) {
          playPromise
            .then(() => card.classList.add('is-playing'))
            .catch(() => card.classList.remove('is-playing'));
        }
      });
    }

    openModal(index) {
      if (!this.modal || !this.modalVideo) return;

      this.previousFocus = document.activeElement;
      this.stopAutoAdvance();
      this.pauseCardVideos();
      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('seq-reels-modal-open');
      this.showModalReel(index, true);
      this.section.querySelector('[data-reels-close]')?.focus({ preventScroll: true });
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;

      this.modalVideo?.pause();
      if (this.modalVideo) {
        this.modalVideo.removeAttribute('src');
        this.modalVideo.innerHTML = '';
        this.modalVideo.load();
      }

      this.modal.hidden = true;
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('seq-reels-modal-open');
      this.previousFocus?.focus?.({ preventScroll: true });
      this.playAllCardPreviews(true);
      this.startAutoAdvance();
    }

    showModalReel(index, preferSound = null) {
      if (!this.modalVideo || !this.cards.length) return;

      if (preferSound === true) this.isMuted = false;
      if (preferSound === false) this.isMuted = true;

      this.modalIndex = (index + this.cards.length) % this.cards.length;
      const card = this.cards[this.modalIndex];
      const sourceVideo = card?.querySelector('video');
      if (!sourceVideo) return;

      this.modalTitle.textContent = card.dataset.reelTitle || '';
      this.modalLocation.textContent = card.dataset.reelLocation || '';
      this.modalLocation.hidden = !card.dataset.reelLocation;

      this.modalVideo.pause();
      this.modalVideo.innerHTML = sourceVideo.innerHTML;
      this.modalVideo.poster = sourceVideo.poster || '';
      this.modalVideo.loop = true;
      this.modalVideo.playsInline = true;
      this.modalVideo.volume = this.volume;
      this.modalVideo.muted = this.isMuted || this.volume === 0;
      this.modalVideo.load();
      this.updateVolumeControls();

      const playAttempt = this.modalVideo.play();
      if (playAttempt?.catch) {
        playAttempt.catch(() => {
          this.isMuted = true;
          this.modalVideo.muted = true;
          this.updateVolumeControls();
          this.modalVideo.play().catch(() => this.updatePlayButton(true));
        });
      }

      this.scrollToCard(this.modalIndex);
    }

    setVolumeFromSlider() {
      if (!this.modalVideo || !this.volumeSlider) return;

      const rawValue = Number(this.volumeSlider.value);
      const volume = Math.min(1, Math.max(0, Number.isFinite(rawValue) ? rawValue / 100 : 0.8));

      this.volume = volume;
      this.modalVideo.volume = volume;

      if (volume === 0) {
        this.isMuted = true;
        this.modalVideo.muted = true;
      } else {
        this.lastAudibleVolume = volume;
        this.isMuted = false;
        this.modalVideo.muted = false;
      }

      this.updateVolumeControls();
      if (this.modalVideo.paused) this.modalVideo.play().catch(() => {});
    }

    toggleSound() {
      if (!this.modalVideo) return;

      if (this.modalVideo.muted || this.isMuted || this.volume === 0) {
        if (this.volume === 0) {
          this.volume = this.lastAudibleVolume || 0.8;
          this.modalVideo.volume = this.volume;
        }
        this.isMuted = false;
        this.modalVideo.muted = false;
      } else {
        this.isMuted = true;
        this.modalVideo.muted = true;
      }

      this.updateVolumeControls();
      if (this.modalVideo.paused) this.modalVideo.play().catch(() => {});
    }

    updateVolumeControls() {
      if (!this.modalVideo) return;

      const muted = this.modalVideo.muted || this.isMuted || this.volume === 0;
      const displayedVolume = muted ? 0 : Math.round(this.volume * 100);

      if (this.soundButton) {
        this.soundButton.classList.toggle('is-muted', muted);
        this.soundButton.setAttribute('aria-label', muted ? 'Turn sound on' : 'Mute video');
      }

      if (this.volumeSlider) {
        this.volumeSlider.value = String(displayedVolume);
        this.volumeSlider.setAttribute('aria-valuetext', muted ? 'Muted' : `${displayedVolume} percent`);
        this.volumeSlider.style.setProperty('--seq-volume-level', `${displayedVolume}%`);
      }

      if (this.volumeValue) {
        this.volumeValue.textContent = muted ? 'Muted' : `${displayedVolume}%`;
      }
    }

    toggleModalPlayback() {
      if (!this.modalVideo) return;
      if (this.modalVideo.paused) {
        this.modalVideo.play().catch(() => {});
      } else {
        this.modalVideo.pause();
      }
    }

    updatePlayButton(paused) {
      if (!this.playButton) return;
      this.playButton.classList.toggle('is-paused', paused);
      this.playButton.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    }

    keepFocusInsideModal(event) {
      const focusable = [...this.modal.querySelectorAll('button:not([disabled]), a[href]')]
        .filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const initialise = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach((section) => new SEQReels(section));
  };

  window.SEQReelsInit = initialise;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
